import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  Container,
  Grid,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HotelIcon from '@mui/icons-material/Hotel';
import SpaIcon from '@mui/icons-material/Spa';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/layout/PageHeader';
import { getCustomerOrders } from '../../services/customer';
import { toCastOrderView, getEntryTimeLabel } from '../../features/cast/castOrderUtils';

/**
 * 顧客のオーダー履歴画面。
 * 既存の orders を customer_id で引いた結果を表示する（履歴テーブルは不要）。
 * 完了オーダーも含めて、その顧客の全オーダーが履歴として残っている。
 */
const CustomerOrderHistory: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);
  const [completedOnly, setCompletedOnly] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getCustomerOrders(Number(userId), completedOnly);
        setData(res);
      } catch (e) {
        console.error('顧客オーダー履歴の取得に失敗:', e);
        setError('オーダー履歴の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userId, completedOnly]);

  const orders: any[] = data?.orders || [];
  const summary = data?.summary || { total_orders: 0, completed_orders: 0, total_grand_total: 0, total_revenue: 0 };

  const statusLabel = (order: any): string => {
    const s = order?.status;
    if (s === 'キャンセル' || s === 'cancelled') return 'キャンセル';
    if (s === 'チェンジ' || s === 'changed') return 'チェンジ';
    if (s === '完了' || s === 'completed') return '完了';
    if (order?.entry_time) return '完了';
    return s || '予約';
  };

  return (
    <DashboardLayout title="顧客オーダー履歴">
      <PageHeader title="顧客オーダー履歴" subtitle={data?.customer_name ? `${data.customer_name} 様` : ''} />
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mb: 2 }}>
          顧客一覧へ戻る
        </Button>

        {/* サマリー */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">総オーダー数</Typography>
              <Typography variant="h6">{summary.total_orders}件</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">利用回数（完了）</Typography>
              <Typography variant="h6">{summary.completed_orders}回</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">累計総額</Typography>
              <Typography variant="h6">¥{Number(summary.total_grand_total).toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">累計収益</Typography>
              <Typography variant="h6">¥{Number(summary.total_revenue).toLocaleString()}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant={completedOnly ? 'outlined' : 'contained'}
            onClick={() => setCompletedOnly(false)}
          >
            すべて
          </Button>
          <Button
            size="small"
            variant={completedOnly ? 'contained' : 'outlined'}
            onClick={() => setCompletedOnly(true)}
          >
            完了のみ
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            オーダー履歴がありません
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {orders.map((order) => {
              const view = toCastOrderView(order);
              const sl = statusLabel(order);
              // 入室時間を主表示にする（予約時間ではなく実際の入室時刻）
              const entryLabel = getEntryTimeLabel(order);
              const dateStr = order.entry_time
                ? new Date(order.entry_time).toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' })
                : '';
              return (
                <Card key={order.id} variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {entryLabel ? `${dateStr} ${entryLabel}` : view.reservationLabel}
                        </Typography>
                      </Stack>
                      <Chip
                        label={sl}
                        size="small"
                        color={sl === '完了' ? 'success' : sl === 'キャンセル' ? 'error' : sl === 'チェンジ' ? 'warning' : 'default'}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SpaIcon fontSize="small" color="action" />
                        <Typography variant="body1">{view.courseLabel}</Typography>
                      </Stack>
                      <Chip label={view.orderTypeLabel} size="small" color="primary" variant="outlined" />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <HotelIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" noWrap>{view.hotelLabel}</Typography>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      キャスト: {view.castNameLabel} / 総額: ¥{Number(order.grand_total || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default CustomerOrderHistory;
